import blobStream from 'blob-stream'
import Konva from 'konva'
import { get } from 'lodash';
import PDFDocument from "pdfkit/js/pdfkit.standalone.js";
import { first } from 'rxjs/operators';
import VueI18n from 'vue-i18n';

import { bus } from '@skinsoft/bus';
import { AliasDataStore, ConditionReport, DefectAlteration, DefectAnnotation, DefectElement, ThesaurusTermDataStore, thesaurusTermSelectors } from '@skinsoft/entity';
import { translatedTitle } from '@skinsoft/entity/src/alias/selectors';
import { comments, date, defects, generalState, interventionsToSchedule, location, medias, referenceNumber, reportReason, reportType, stateOfConservation, technicsAndMaterials, treatmentPriority } from '@skinsoft/entity/src/condition-report/selectors';
import { i18n } from '@skinsoft/i18n';
import { getImageOrientation, MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import { getReadableDate, getTranslatedString, SearchFeatured } from '@skinsoft/ui';
import { isImageOrientationAutoManaged } from '@skinsoft/utils'

import { PDF_STEP_COMPLETE_TOPIC } from '../../../busTopics';
import { dateTimeFormats, messages } from '../../../locales';
import { SignatureDrawing, SignerInputCard } from '../../component/signature-module/signer-input-card';
import { DrawingToolType } from '../../report-edition/drawing-editor-components/toolbox';
import { getLabelCoordinates } from '../../report-edition/drawing-editor-components/toolbox/label-coordinates-helper';
import './pdf-document-extension-methods'

enum AlterationsDisplayMode {
    SUMMARY,
    WITH_THUMBNAILS,
    FULL_PAGE
}

export class PDFGenerator {

    // Layout constants
    public static readonly TITLE_FONT_SIZE = 19;
    public static readonly SECTION_FONT_SIZE = 16;
    public static readonly BODY_TEXT_FONT_SIZE = 14;
    public static readonly FOOTER_FONT_SIZE = 10;
    public static readonly DOCUMENT_MARGIN = 35;
    public static readonly A4_PAPER_SIZE = [595.28, 841.89];
    public static readonly DEFAULT_IMAGE_SIZE = 160;
    public static readonly FULL_PAGE_IMAGE_SIZE = 400;
    public static readonly IMAGE_BETWEEN_GAP = 15;
    public static readonly IMAGE_TEXT_BETWEEN_GAP = 30;
    public static readonly SIGNATURE_IMAGE_WIDTH = 250;
    public static readonly IMAGE_GENERATION_TARGET_PIXEL_NUMBER = 1500000;
    public static readonly SIGNATURE_GENERATION_PIXEL_RATIO = 0.5;
    public static readonly SIGNATURE_IMAGE_HEIGHT = PDFGenerator.SIGNATURE_IMAGE_WIDTH *
        SignerInputCard.DEFAULT_SIGNATURE_CANVAS_HEIGHT / SignerInputCard.DEFAULT_SIGNATURE_CANVAS_WIDTH;

    public static readonly REGULAR_FONT: string = "Helvetica";
    public static readonly BOLD_FONT: string = "Helvetica-Bold";

    private static hiddenCanvasContainer: HTMLDivElement = null as any;
    private static hiddenStage: Konva.Stage = null as any;
    private static hiddenStageShapesLayer: Konva.FastLayer = null as any;
    private static hiddenStageAnnotationsLayer: Konva.FastLayer = null as any;

    // For annotations, see their equivalents in the drawing editor
    private static readonly STROKE_WIDTH_RATIO: number = 0.01;
    private static readonly ANNOTATION_LABEL_RADIUS_RATIO = 0.04;
    private static readonly ANNOTATION_LABEL_FONT_SIZE_RATIO = 0.04;
    private static readonly ANNOTATION_LABEL_STROKE_WIDTH_RATIO = 0.004;

    private static $i18n: VueI18n;

    private static pageNumber: number = 0;
    private static conditionReport: ConditionReport = null as any;
    private static searchFeatured: SearchFeatured = null as any;

    // tslint:disable-next-line:member-ordering
    public static generatePDF(conditionReport, searchFeatured, onPdfGenerated) {
        this.createHiddenCanvas();

        const doc = new PDFDocument({
            bufferPages: true,
            margin: this.DOCUMENT_MARGIN,
            size: this.A4_PAPER_SIZE,
        });

        const stream = doc.pipe(blobStream());
        this.conditionReport = conditionReport;
        this.searchFeatured = searchFeatured;
        this.drawPDF(doc)
        stream.on('finish', () => {
            const blob = stream.toBlob('application/pdf');

            // @TODO manage cache
            // const cache = new IndexedDBCache();
            // cache.write(conditionReport.id, blob)
            onPdfGenerated(blob)
        });
    }

    private static addFooters(doc) {
        const bottom = doc.page.margins.bottom;
        const footerPrefix = this.$t('pdfDocument.footer.prefix')
        const footerSeparator = this.$t('pdfDocument.footer.separator')
        const range = doc.bufferedPageRange();

        for (let pageIndex = range.start; pageIndex < range.count; pageIndex++) {
            doc.switchToPage(pageIndex)
            // cancelling margin to be able to write into it
            doc.page.margins.bottom = 0;
            const pageNumber = pageIndex + 1;
            doc
                .fill("black")
                .font(this.REGULAR_FONT)
                .fontSize(this.FOOTER_FONT_SIZE)
                .text(`${footerPrefix} ${referenceNumber(this.conditionReport)} ${footerSeparator} ${pageNumber}/${range.count}`, 20, doc.page.height - this.DOCUMENT_MARGIN, {
                    align: 'right',
                });
            // restoring margin
            doc.page.margins.bottom = bottom;
        }
    }

    /**
     * Creates a unique hidden canvas to perform drawing operations
     * before exporting images to pdf.
     */
    private static createHiddenCanvas() {
        if (!this.hiddenCanvasContainer) {
            this.hiddenCanvasContainer = document.createElement("div");
            this.hiddenCanvasContainer.id = 'container';
            this.hiddenCanvasContainer.hidden = true;
            document.body.appendChild(this.hiddenCanvasContainer);

            // Creating konva stage to render image
            const stageConfig = {
                container: 'container',
                height: 50,
                width: 50,
            }

            this.hiddenStage = new Konva.Stage(stageConfig);
            this.hiddenStageShapesLayer = new Konva.FastLayer();
            this.hiddenStageAnnotationsLayer = new Konva.FastLayer();
            this.hiddenStage.add(this.hiddenStageShapesLayer)
            this.hiddenStage.add(this.hiddenStageAnnotationsLayer)
        }
    }

    private static resetHiddenCanvases() {
        this.hiddenStageShapesLayer.destroyChildren()
        this.hiddenStageAnnotationsLayer.destroyChildren()
    }

    private static async drawPDF(doc) {

        // Initializing i18n, don't call this.$t before this has been executed
        await i18n(messages, dateTimeFormats).then(value => {
            this.$i18n = value;
        })

        this.createDocumentHeader(doc, referenceNumber(this.conditionReport) as string)

        await this.createItemSection(doc, this.searchFeatured)
        bus.emit(PDF_STEP_COMPLETE_TOPIC, { step: 2 })

        await this.createContextSection(doc)
        bus.emit(PDF_STEP_COMPLETE_TOPIC, { step: 3 })

        await this.createReportSection(doc);
        bus.emit(PDF_STEP_COMPLETE_TOPIC, { step: 4 })

        const defectElements = defects(this.conditionReport)
        if (this.shallGenerateAlterationsSection(defectElements)) {
            await this.createAlterationsSection(doc, defectElements);
            await this.createDetailedAlterationsSection(doc, defectElements)
        }
        bus.emit(PDF_STEP_COMPLETE_TOPIC, { step: 5 })

        this.addFooters(doc);
        doc.end();
    }

    private static async createDocumentHeader(doc, conditionReportTitle) {
        doc.moveDown(8);
        doc.font(this.BOLD_FONT)
        const titlePrefix = this.$t('pdfDocument.mainTitlePrefix')

        doc.fontSize(this.TITLE_FONT_SIZE).text(`${titlePrefix}${conditionReportTitle}`, {
            align: 'center'
        });
    }

    private static async createItemSection(doc, searchFeatured: SearchFeatured) {

        let indentation = this.DOCUMENT_MARGIN;
        let textWidth = doc.page.width - 2 * this.DOCUMENT_MARGIN;

        doc.sectionTitle(this.$t('pdfDocument.objectSection.sectionTitle'));
        doc.moveDown(2)
        const yImage = doc.y;
        if (searchFeatured.media) {
            indentation = this.DEFAULT_IMAGE_SIZE + this.IMAGE_TEXT_BETWEEN_GAP + this.DOCUMENT_MARGIN;
            textWidth = doc.page.width - indentation - this.DOCUMENT_MARGIN;
            await this.addImageFromMediaId(doc, searchFeatured.media)
        }

        let alias: string = searchFeatured.credits ? searchFeatured.credits.alias : "";
        const authorFunction: string = searchFeatured.credits ? searchFeatured.credits.authorFunction : "";
        if (alias) {
            alias = getTranslatedString(translatedTitle(await AliasDataStore.getInstance().fetchOne(alias).pipe(first()).toPromise()), this.$i18n.locale)
        }
        const authorSummary = (alias || "") + (alias && authorFunction ? ` (${authorFunction})` : "")

        doc.y = yImage;
        doc.x = indentation;
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.inventoryNumber'), searchFeatured.inventoryNumber, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.title'), searchFeatured.title, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.author'), authorSummary, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.creationDate'), searchFeatured.creation_date, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.typology'), searchFeatured.domain, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.denomination'), searchFeatured.denomination, { width: textWidth })
        if (searchFeatured.materialAndTechniques && searchFeatured.materialAndTechniques.length > 0) {
            doc.boldText(this.$t('pdfDocument.objectSection.materialsAndTechniques'), { width: textWidth })
            for (const mt of searchFeatured.materialAndTechniques) {
                doc.regularText(`- ${mt}`, { width: textWidth, indent: 10, paragraphGap: 8 })
            }
        }
        if (searchFeatured.measurements && searchFeatured.measurements.length > 0) {
            doc.boldText(this.$t('pdfDocument.objectSection.dimensions'), { width: textWidth })
            for (const measurement of searchFeatured.measurements) {
                doc.regularText(`- ${measurement}`, { width: textWidth, indent: 10 })
            }

        }
        doc.moveDown()
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.institution'), searchFeatured.institution, { width: textWidth })
    }

    private static async createContextSection(doc) {
        doc.addPage()
        doc.sectionTitle(this.$t('pdfDocument.contextSection.sectionTitle'))

        // Medias
        doc.moveDown(1);
        const associatedMedias = medias(this.conditionReport);
        await this.addImageBlock(doc, associatedMedias)

        // Report type
        const resolvedReportType = await this.getThesaurusTermFromId(reportType(this.conditionReport))
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportType'), resolvedReportType)

        // Report type
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportReason'), reportReason(this.conditionReport, this.$i18n.locale))

        // Report date
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportDate'), getReadableDate(date(this.conditionReport)))

        // Report location
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportPlace'), location(this.conditionReport, this.$i18n.locale))

        // Materials and techniques
        doc.boldText(this.$t('pdfDocument.contextSection.matAndTechs'))
        doc.moveDown(0.6);
        doc.regularText(technicsAndMaterials(this.conditionReport, this.$i18n.locale), { align: "justify" })
        doc.moveDown(1);

        // Comments
        doc.boldText(this.$t('pdfDocument.contextSection.comments'))
        doc.moveDown(0.8);
        doc.regularText(generalState(this.conditionReport, this.$i18n.locale), { align: "justify" })
    }

    private static async createAlterationsSection(doc, defectElements: DefectElement[]) {
        doc.addPage()
        doc.sectionTitle(this.$t('pdfDocument.alterationsSection.sectionTitle'))

        await this.generateAlterationSection(doc, defectElements, AlterationsDisplayMode.SUMMARY)
        doc.moveUp()
        doc.softSeparationLine()
        await this.generateAlterationSection(doc, defectElements, AlterationsDisplayMode.WITH_THUMBNAILS)
    }

    private static async createDetailedAlterationsSection(doc, defectElements: DefectElement[]) {
        await this.generateAlterationSection(doc, defectElements, AlterationsDisplayMode.FULL_PAGE)
    }

    private static async createReportSection(doc) {
        doc.addPage()
        doc.sectionTitle(this.$t('pdfDocument.reportSection.sectionTitle'))

        doc.moveDown(1)
        const theComments = comments(this.conditionReport, this.$i18n.locale)
        if (theComments) {
            doc.regularText(theComments, { align: "justify" })
        }

        doc.moveDown(1)
        const conservationStates = await stateOfConservation(this.conditionReport)
        doc.boldText(this.$t('pdfDocument.reportSection.stateOfConservation'));
        doc.moveDown(0.5)
        if (conservationStates) {
            doc.x = this.DOCUMENT_MARGIN + 8;
            for (const state of conservationStates) {
                const stateTh = await this.getThesaurusTermFromId(state.stateOfConservation);
                doc.boldText(stateTh)
                doc.regularText(getTranslatedString(state.precision, this.$i18n.locale), { align: "justify" })
                doc.moveDown(0.5)
            }
            doc.x = this.DOCUMENT_MARGIN;
        }
        doc.moveDown()
        const priorityOfTreatment = await this.getThesaurusTermFromId(treatmentPriority(this.conditionReport))
        if (priorityOfTreatment) {
            doc.boldText(this.$t('pdfDocument.reportSection.treatmentPriority'), { continued: true });
            doc.regularText(priorityOfTreatment)
            doc.moveDown(1)
        }

        const theInterventionsToSchedule: any = await interventionsToSchedule(this.conditionReport)
        if (theInterventionsToSchedule) {
            doc.boldText(this.$t('pdfDocument.reportSection.interventionsToSchedule'));
            doc.moveDown(0.5)
            doc.x = this.DOCUMENT_MARGIN + 8;
            for (const intervention of theInterventionsToSchedule) {
                const interventionType = await this.getThesaurusTermFromId(get(intervention, "typeIntervention"))
                if (interventionType) {
                    doc.boldText(interventionType)
                }
                doc.regularText(getTranslatedString(intervention.precision, this.$i18n.locale), { align: "justify" })
                doc.moveDown(0.5)

            }
            doc.x = this.DOCUMENT_MARGIN;

            doc.moveDown(1)
        }
        doc.moveDown(3)
        doc.boldText(this.$t('pdfDocument.reportSection.signatoriesTitle'));
        doc.moveDown(1)
        // signatures
        const signers = get(this.conditionReport, 'conditionReportCS.signers')
        let lineY = doc.y;
        if (signers) {
            for (const signer of signers) {
                if (doc.y !== lineY) {
                    doc.moveDown(2);
                    lineY = doc.y;
                }
                const headerY = doc.y;
                doc
                    .fill('black')
                    .text(getTranslatedString(signer.signer, this.$i18n.locale), { width: PDFGenerator.SIGNATURE_IMAGE_WIDTH })
                if (signer.signature) {
                    const unMarshalledSignature = JSON.parse(signer.signature);
                    if (unMarshalledSignature
                        && unMarshalledSignature.signature
                        && unMarshalledSignature.signature.length > 0) {
                        this.generateSignature(doc, unMarshalledSignature, headerY)
                    } else {
                        this.generateSignature(doc, null, headerY)
                    }
                }
            }
        }
    }

    private static async addImageBlock(doc, mediasIds) {
        doc.moveDown(1)
        const imgStep = this.DEFAULT_IMAGE_SIZE + this.IMAGE_BETWEEN_GAP;
        for (const mediaId of mediasIds) {
            await this.addImageFromMediaId(doc, mediaId)

            doc.x += imgStep;

            // Continuing on a new line if needed
            if (doc.x + imgStep > doc.page.width - this.DOCUMENT_MARGIN) {
                doc.x = this.DOCUMENT_MARGIN;
                doc.y += imgStep;
            }

            // Continuing on a new page if needed
            if (doc.y + imgStep > doc.page.height - this.DOCUMENT_MARGIN) {
                doc.addPage()
                doc.x = this.DOCUMENT_MARGIN;
                doc.y += this.DOCUMENT_MARGIN;
            }
        }
        if (doc.x !== this.DOCUMENT_MARGIN) {
            doc.x = this.DOCUMENT_MARGIN;
            doc.y += imgStep;
        }

    }

    private static async addImageFromMediaId(doc, mediaId, x?: number, y?: number, options?: any) {
        const saveY = doc.y;
        doc.save();
        const imgX = x ? x : doc.x;
        const imgY = y ? y : doc.y;

        const centerX = imgX + this.DEFAULT_IMAGE_SIZE / 2;
        const centerY = imgY + this.DEFAULT_IMAGE_SIZE / 2;

        const imgOptions = options ? options :
            {
                align: 'center',
                fit: [this.DEFAULT_IMAGE_SIZE, this.DEFAULT_IMAGE_SIZE],
                valign: 'center'
            };

        const mediaData = await MediaTransferHelper.getInstance().downloadMedia(mediaId).toPromise();
        const rotationAngle = await PDFGenerator.getRotationAngleFromOrientation(mediaData.dataUrl)

        doc.rect(imgX, imgY, this.DEFAULT_IMAGE_SIZE, this.DEFAULT_IMAGE_SIZE).fill("lightgray");
        doc
            .rotate(rotationAngle, { origin: [centerX, centerY] })
            .image(mediaData.dataUrl, imgX, imgY, imgOptions)
        doc.restore()
        doc.y = saveY;
    }

    private static async getRotationAngleFromOrientation(imageDataUrl) {

        const img = new Image();
        img.src = imageDataUrl;
        const orientation = await getImageOrientation(img)
        switch (orientation) {
            case 3:
                return 180
            case 6:
                return 90
            case 8:
                return -90
            default:
                return 0
        }
    }

    private static generateSignature(doc, signatureDrawingData: SignatureDrawing | null, headerY) {

        if (signatureDrawingData) {
            // Resizing konva stage with signature format
            this.hiddenStage.width(signatureDrawingData.canvas.width)
            this.hiddenStage.height(signatureDrawingData.canvas.height)
            this.resetHiddenCanvases()
            signatureDrawingData.signature.map(signatureShape => {
                this.hiddenStageShapesLayer.add(new Konva.Line(signatureShape));
            })
            this.hiddenStage.add(this.hiddenStageShapesLayer)
            this.hiddenStageShapesLayer.draw();
            const signatureImgDataUrl = this.hiddenStage.toDataURL({ pixelRatio: this.SIGNATURE_GENERATION_PIXEL_RATIO });

            doc.image(signatureImgDataUrl, doc.x, doc.y, {
                align: 'center',
                height: this.SIGNATURE_IMAGE_HEIGHT,
                valign: 'center',
                width: signatureDrawingData.canvas.width * this.SIGNATURE_IMAGE_HEIGHT / signatureDrawingData.canvas.height,
            })
        }
        doc.x += this.SIGNATURE_IMAGE_WIDTH + this.IMAGE_BETWEEN_GAP
        doc.y = headerY;
        if (doc.x + this.SIGNATURE_IMAGE_WIDTH > doc.page.width - this.DOCUMENT_MARGIN) {

            doc.x = this.DOCUMENT_MARGIN;
            doc.y += this.SIGNATURE_IMAGE_HEIGHT + this.IMAGE_BETWEEN_GAP;
            doc.moveDown(1)
        }
    }

    private static async getThesaurusTermFromId(id): Promise<string> {
        if (id) {
            return getTranslatedString(thesaurusTermSelectors.preferredTerm(
                await ThesaurusTermDataStore.getInstance().fetchOne(id).pipe(first()).toPromise()
            ),
                this.$i18n.locale)
        } else {
            return ""
        }
    }

    /**
     * Displays the alterations as a tree with images or as a summary, according to
     * 'withDetails' parameter
     * @param doc 
     * @param reportDefects 
     * @param displayMode
     */
    private static async generateAlterationSection(doc, reportDefects, displayMode: AlterationsDisplayMode = AlterationsDisplayMode.SUMMARY) {
        doc.moveDown(1)
        let firstPage: boolean = true;
        if (reportDefects) {
            // Looping over elements
            for (const element of reportDefects) {
                firstPage = false;
                doc.x = this.DOCUMENT_MARGIN;
                doc.fill("black")
                const elementName = getTranslatedString(element.element, this.$i18n.locale);
                const elementPrecision = getTranslatedString(element.precision, this.$i18n.locale)

                const elementSummary = (elementName || "") + (elementPrecision ? ` (${elementPrecision})` : "");

                const observations = element.observationAlterationDetails;
                if (displayMode !== AlterationsDisplayMode.FULL_PAGE) {
                    doc.boldText(elementSummary)
                }
                doc.moveDown(0.5)
                if (observations) {
                    for (const observation of observations) {
                        const materials = observation.materialAlterations;
                        if (materials) {
                            for (const material of materials) {
                                let typeOfObs = "";
                                let materialName = "";
                                if (observation.typeOfObservation) {
                                    typeOfObs = await this.getThesaurusTermFromId(observation.typeOfObservation)
                                }
                                if (material.material) {
                                    materialName = await this.getThesaurusTermFromId(material.material);
                                }
                                doc.x = this.DOCUMENT_MARGIN;
                                // const secondLevelDesc = `${typeOfObs || "..."} > ${materialName || "..."}`
                                const secondLevelDesc = typeOfObs + (typeOfObs && materialName ? " > " : "") + materialName
                                doc.breakContinue()
                                doc.moveDown()

                                switch (displayMode) {
                                    case AlterationsDisplayMode.WITH_THUMBNAILS:
                                        if (secondLevelDesc) {
                                            doc.regularText(secondLevelDesc, { indent: 0 })
                                            doc.moveDown()
                                        }
                                        break;
                                    case AlterationsDisplayMode.SUMMARY:
                                        if (secondLevelDesc) {
                                            doc.regularText(`- ${secondLevelDesc} : `, { indent: 8, continued: true })
                                        }
                                        break;
                                }
                                if (material.alterations) {
                                    let firstAlteration = true;
                                    for (const alteration of material.alterations) {
                                        if (displayMode !== AlterationsDisplayMode.FULL_PAGE) {
                                            this.maybeOnANewPage(alteration, doc);
                                        }
                                        doc.x = this.DOCUMENT_MARGIN;
                                        let typeOfAlteration: string = "";
                                        if (alteration.typeOfAlteration) {
                                            typeOfAlteration = await this.getThesaurusTermFromId(alteration.typeOfAlteration);
                                        }
                                        const alterationPrecision: string = getTranslatedString(alteration.precision, this.$i18n.locale);

                                        if (displayMode !== AlterationsDisplayMode.FULL_PAGE) {
                                            const withDetails = displayMode === AlterationsDisplayMode.WITH_THUMBNAILS;
                                            doc
                                                .fill("#f15f22")
                                                .boldText(`${firstAlteration || withDetails ? "" : ","}  ${typeOfAlteration}`, { continued: !!alterationPrecision || !withDetails })
                                        }
                                        if (alterationPrecision) {
                                            switch (displayMode) {
                                                case AlterationsDisplayMode.SUMMARY:
                                                    doc
                                                        .fill("black")
                                                        .regularText(" (" + alterationPrecision + ")", { continued: true })
                                                    break;
                                                case AlterationsDisplayMode.WITH_THUMBNAILS:
                                                    doc
                                                        .fill("black")
                                                        .regularText(" (" + alterationPrecision + ")")
                                                    break;
                                            }
                                        }

                                        if (displayMode === AlterationsDisplayMode.WITH_THUMBNAILS ||
                                            displayMode === AlterationsDisplayMode.FULL_PAGE) {
                                            await this.drawAnnotations(doc, alteration.annotations, displayMode, {
                                                elementSummary,
                                                typeOfObs,
                                                // tslint:disable-next-line:object-literal-sort-keys
                                                materialName,
                                                typeOfAlteration,
                                                alterationPrecision
                                            })
                                            doc.moveDown(1)
                                        }
                                        firstAlteration = false;
                                    }
                                    doc.breakContinue()
                                    doc.moveDown()
                                }
                                doc.moveDown()
                            }
                        }
                    }
                }
                doc.moveDown(1)
            }
        }
        doc.moveDown(1)
    }

    private static async drawAnnotations(doc, annotations, displayMode: AlterationsDisplayMode, annotationPathElems?: any) {
        let shouldGoToNewLine = true;
        if (annotations && annotations.length) {
            let annotationIndex = 0;
            for (const annotation of annotations) {
                shouldGoToNewLine = true
                if (annotation.media) {
                    this.resetHiddenCanvases()

                    // Retrieving image data
                    const mediaId = annotation.media;
                    const media = await MediaTransferHelper.getInstance().downloadMedia(mediaId, 'proxy').pipe(first()).toPromise()
                    const img = new Image();
                    const imgSize: { w, h } = await (new Promise((resolve, reject) => {
                        img.onload = () => {
                            resolve({ w: img.width, h: img.height })
                        }
                        img.onerror = reject
                        img.src = media.dataUrl;
                    }))

                    // Retrieving drawing data
                    const svg = JSON.parse(annotation.svg);

                    // Drawing bg image
                    const imgConfig = {
                        height: imgSize.h,
                        image: img,
                        width: imgSize.w,
                        x: 0,
                        y: 0,
                    }
                    const fixedConfig = await this.fixImageOrientation(imgConfig)
                    this.hiddenStageShapesLayer.add(new Konva.Image(fixedConfig))

                    // Drawing all shapes with labels
                    let index: number = 1;
                    for (const shapeSvg of svg) {
                        const drawnShape = this.getShapeFromSvg(shapeSvg);
                        if (shapeSvg) {
                            this.hiddenStageShapesLayer.add(drawnShape);
                            const labelRadius = PDFGenerator.ANNOTATION_LABEL_RADIUS_RATIO * imgConfig.width;
                            const labelCenter = getLabelCoordinates(shapeSvg, labelRadius)
                            const labelNode = new Konva.Circle({
                                fill: "white",
                                radius: labelRadius,
                                stroke: "black",
                                strokeWidth: PDFGenerator.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * imgConfig.width,
                                x: labelCenter.x,
                                y: labelCenter.y,
                            })

                            const textNode = new Konva.Text({
                                align: 'left',
                                fill: 'black',
                                fontFamily: 'Calibri',
                                fontSize: PDFGenerator.ANNOTATION_LABEL_FONT_SIZE_RATIO * imgConfig.width,
                                offsetX: 0,
                                offsetY: 0,
                                text: (index++).toString(),
                                x: labelCenter.x,
                                y: labelCenter.y,
                            });

                            this.hiddenStageAnnotationsLayer.add(labelNode);
                            this.hiddenStageAnnotationsLayer.add(textNode);

                            // centering label in circle
                            textNode.offsetX(textNode.width() / 2)
                            textNode.offsetY(textNode.height() / 2)
                        }
                    }
                    this.hiddenStageShapesLayer.draw();
                    this.hiddenStageAnnotationsLayer.draw();
                    const pixelRatio = this.getPixelRatio(imgConfig.width, imgConfig.height)
                    const generatedImgDataUrl = this.hiddenStage.toDataURL({
                        pixelRatio
                    })
                    const lineY = doc.y;

                    // Drawing image on pdf
                    switch (displayMode) {
                        case AlterationsDisplayMode.WITH_THUMBNAILS:
                            doc.rect(doc.x, doc.y, this.DEFAULT_IMAGE_SIZE, this.DEFAULT_IMAGE_SIZE).fill("lightgray");
                            doc.image(generatedImgDataUrl, doc.x, doc.y, {
                                align: 'center',
                                fit: [this.DEFAULT_IMAGE_SIZE, this.DEFAULT_IMAGE_SIZE],
                                valign: 'center'
                            })
                            doc.x += this.DEFAULT_IMAGE_SIZE + this.IMAGE_BETWEEN_GAP;
                            doc.y = lineY;
                            // If line overflows on the right, go on a new line
                            if (doc.x + this.DEFAULT_IMAGE_SIZE > doc.page.width - this.DOCUMENT_MARGIN) {
                                doc.x = this.DOCUMENT_MARGIN
                                doc.y += this.DEFAULT_IMAGE_SIZE + this.IMAGE_BETWEEN_GAP
                                shouldGoToNewLine = false;
                            }

                            // If line overflows vertically, go on a new page
                            if (doc.y + this.DEFAULT_IMAGE_SIZE > doc.page.height - this.DOCUMENT_MARGIN) {
                                doc.addPage()
                                doc.x = this.DOCUMENT_MARGIN;
                                doc.y = this.DOCUMENT_MARGIN;
                                shouldGoToNewLine = false;
                            }
                            break;

                        case AlterationsDisplayMode.FULL_PAGE:
                            doc.addPage()
                            doc.sectionTitle(this.$t('pdfDocument.alterationsSection.detailedAlteration'))
                            doc.moveDown()

                            // Alteration path
                            if (annotationPathElems.elementSummary) {
                                doc.boldText(`${annotationPathElems.elementSummary} > `, { continued: true })
                            }
                            if (annotationPathElems.typeOfObs) {
                                doc.regularText(`${annotationPathElems.typeOfObs} > `, { continued: true })
                            }
                            if (annotationPathElems.materialName) {
                                doc.regularText(`${annotationPathElems.materialName} > `, { continued: true })
                            }
                            if (annotationPathElems.typeOfAlteration) {
                                doc.fill("#f15f22")
                                doc.boldText(`${annotationPathElems.typeOfAlteration}`, { continued: true })
                            }
                            if (annotationPathElems.alterationPrecision) {
                                doc.fill("black")
                                doc.regularText(` (${annotationPathElems.alterationPrecision})`, { continued: false })
                            } else {
                                doc.moveDown()
                            }
                            doc.breakContinue()
                            doc.moveDown()

                            const fpLineY = doc.y
                            const pageWidth = doc.page.width - 2 * this.DOCUMENT_MARGIN;
                            doc.image(generatedImgDataUrl, doc.x, doc.y, {
                                align: 'center',
                                fit: [pageWidth, this.FULL_PAGE_IMAGE_SIZE],
                                valign: 'center'
                            })

                            // Caption
                            doc.y = fpLineY + this.FULL_PAGE_IMAGE_SIZE
                            doc.moveDown(2)
                            if (annotation.title) {
                                doc.regularText(getTranslatedString(annotation.title, this.$i18n.locale), { align: "center" })
                            }

                            // Comments
                            if (annotation.comments) {
                                // doc.moveDown(2)
                                let commentIndex = 1;
                                for (const comment of annotation.comments) {
                                    doc.fill("black")
                                    doc.boldText(`${this.$t('pdfDocument.alterationsSection.observationLabel')} ${commentIndex} : `, { continued: true })
                                    doc.regularText(getTranslatedString(comment, this.$i18n.locale), {
                                        continued: false,
                                        paragraphGap: 8,
                                    })
                                    commentIndex++;
                                }
                            }
                            doc.y = 0 // to avoid blank pages
                            break;
                    }
                }
                annotationIndex++;
            }
            if (shouldGoToNewLine) {
                doc.y += this.DEFAULT_IMAGE_SIZE;
            }
        }
        else {
            doc.regularText()
        }
    }

    /**
     * Estimates the height of the future annotation block and
     * adds a new page if it doesn't fit in the current one
     * @param alteration 
     * @param doc 
     */
    private static maybeOnANewPage(alteration: DefectAlteration, doc) {
        // Take number if medias into account
        const estimatedHeight = alteration.annotations && alteration.annotations.length ? this.DEFAULT_IMAGE_SIZE * 1.2 : 50;
        const overflowY: boolean = doc.page.height - this.DOCUMENT_MARGIN - doc.y < estimatedHeight;
        if (overflowY) {
            doc.addPage()
        }
    }

    private static shallGenerateAlterationsSection(defectElements: DefectElement[]) {
        for (const element of defectElements) {
            // If element level is not empty
            if (getTranslatedString(element.element, this.$i18n.locale)
                || getTranslatedString(element.precision, this.$i18n.locale)) {
                return true;
            }

            for (const observation of element.observationAlterationDetails) {
                // If observations level is not empty
                if (observation.typeOfObservation) {
                    return true;
                }

                for (const material of observation.materialAlterations) {
                    // If material level is not empty
                    if (material.material) {
                        return true;
                    }


                    for (const alteration of material.alterations) {
                        // If alterations level is not empty
                        if (alteration.typeOfAlteration
                            || getTranslatedString(alteration.precision, this.$i18n.locale)) {
                            return true;
                        }
                        for (const annotation of alteration.annotations) {
                            // If annotation level is not empty
                            if (annotation.media) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    private static getPixelRatio(imgWidth: number, imgHeight: number) {
        const ratio = PDFGenerator.IMAGE_GENERATION_TARGET_PIXEL_NUMBER / (imgWidth * imgHeight);
        return ratio < 1 ? ratio : 1;
    }

    private static async fixImageOrientation(baseImageConfig) {
        const orientation = isImageOrientationAutoManaged() ? 1 : await getImageOrientation(baseImageConfig.image)
        switch (orientation) {
            case 3: {
                this.hiddenStage.width(baseImageConfig.width)
                this.hiddenStage.height(baseImageConfig.height)

                return {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: baseImageConfig.height,
                    rotation: 180
                }
            }
            case 6: {
                this.hiddenStage.width(baseImageConfig.height)
                this.hiddenStage.height(baseImageConfig.width)

                return {
                    ...baseImageConfig,

                    offsetX: 0,
                    offsetY: baseImageConfig.height,
                    rotation: 90
                }
            }
            case 8: {
                this.hiddenStage.width(baseImageConfig.height)
                this.hiddenStage.height(baseImageConfig.width)

                return {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: 0,
                    rotation: -90,
                }
            }
            default:
                this.hiddenStage.width(baseImageConfig.width)
                this.hiddenStage.height(baseImageConfig.height)
                return baseImageConfig;
        }
    }

    private static getShapeFromSvg(shapeSvg) {
        switch (shapeSvg.type) {
            case DrawingToolType.RECTANGLE:
                return new Konva.Rect(shapeSvg);
            case DrawingToolType.CIRCLE:
                return new Konva.Circle(shapeSvg)
            case DrawingToolType.FREE_DRAWING:
                return new Konva.Line(shapeSvg)
            case DrawingToolType.ARROW:
                return new Konva.Arrow(shapeSvg)
            default:
                return new Konva.Circle(shapeSvg)
        }
    }

    private static $t(value: string) {
        return this.$i18n.t(value, this.$i18n.locale);
    }
}



